import React, { useContext, useMemo, useState } from 'react'
import Assets from '@ui/assets'
import { SecondaryText, TitleXl } from '@ui/text'
import { Input } from '@ui/input'
import { Button } from '@ui/buttons'
import { Nav, NavListItem } from '@ui/nav'
import {
  Container,
  LoginContainer,
  LoginImage,
  LoginForm,
  ButtonsRow,
  LogoContainer,
  ErrorMessage,
  InfoMessage,
  LocaleContainer,
  SecondaryActionButton,
} from 'components/Login'
import { __ } from 'services/locale'
import localeContext from 'contexts/locale'
import api from 'services/api'
import { UnauthorizedError } from 'services/api/errors/auth'
import { getPrefillEmail } from 'services/login-link'
import { apiErrorHandler } from 'utils/error'
import errorContext from 'contexts/error'
import authContext from 'contexts/auth'
import config from 'config'

interface Props {
  /** set when the app was opened through a login link that could not be used */
  loginLinkInvalid?: boolean
}

// The link button does not submit the form, so the browser does not check the
// address for us. Deliberately lax, the server decides who exists.
const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^.@][^@\s]*$/u

const Login = ({ loginLinkInvalid }: Props) => {
  const [loading, setLoading] = useState(false)
  const [linkLoading, setLinkLoading] = useState(false)
  const [linkSentTo, setLinkSentTo] = useState('')
  const [linkErr, setLinkErr] = useState('')
  const [inputs, updateInputs] = useState({
    // support sends a link with #username=... so that this field does not have
    // to be typed in at all
    email: getPrefillEmail(),
    password: '',
  })
  const [err, setErr] = useState('')
  const localeCtx = useContext(localeContext)
  const errorCtx = useContext(errorContext)
  const authCtx = useContext(authContext)

  const locales = useMemo(() => config.locale.available.map(lang =>
    <NavListItem
      key={lang}
      active={lang === localeCtx.lang}
      onClick={() => localeCtx.setLanguage(lang)}
    >
      {lang.toUpperCase()}
    </NavListItem>), [localeCtx])

  const requestLoginLink = async () => {
    const email = inputs.email.trim()
    setLinkErr('')
    setLinkSentTo('')

    if (!EMAIL_RE.test(email)) {
      setLinkErr(__('login.link-email', localeCtx.lang))
      return
    }

    setLinkLoading(true)
    try {
      await api.requestLoginLink(email, localeCtx.lang)
      setLinkSentTo(email)
    } catch (error) {
      setLinkErr(__('login.link-error', localeCtx.lang))
    } finally {
      setLinkLoading(false)
    }
  }

  return (
    <Container>
      <LoginContainer>
        <LogoContainer>
          <Assets.Logo/>
        </LogoContainer>
        <TitleXl>{__('login.title', localeCtx.lang)}</TitleXl>
        <SecondaryText>
          {__('login.text', localeCtx.lang)}
        </SecondaryText>
        <LoginForm onSubmit={async evt => {
          evt.preventDefault()
          setLoading(true)
          setErr('')
          try {
            await api.login(inputs.email, inputs.password)
            authCtx.setAuthenticated(true)
          } catch (error) {
            if (error instanceof UnauthorizedError) {
              setErr(__('login.incorrect', localeCtx.lang))
            } else {
              apiErrorHandler(errorCtx, localeCtx, authCtx)(error)
            }
            setLoading(false)
          }
        }}>
          <Input
            name="email"
            type="text"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={__('login.email', localeCtx.lang)}
            value={inputs.email}
            onChange={evt => updateInputs({ ...inputs, email: evt.target.value })}
          />
          <Input
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder={__('login.password', localeCtx.lang)}
            value={inputs.password}
            onChange={evt => updateInputs({ ...inputs, password: evt.target.value })}
          />
          <ButtonsRow>
            <SecondaryActionButton
              type="button"
              onClick={requestLoginLink}
              disabled={!inputs.email.trim() || linkLoading}
            >
              {__('login.link-button', localeCtx.lang)}
            </SecondaryActionButton>
            <Button loading={loading}>{__('login.submit', localeCtx.lang)}</Button>
          </ButtonsRow>
        </LoginForm>
        {err && <ErrorMessage>{err}</ErrorMessage>}
        {linkErr && <ErrorMessage>{linkErr}</ErrorMessage>}
        {loginLinkInvalid && <ErrorMessage>{__('login.link-invalid', localeCtx.lang)}</ErrorMessage>}
        {linkSentTo && (
          <InfoMessage>
            {__('login.link-sent', localeCtx.lang).replace('%s', linkSentTo)}
          </InfoMessage>
        )}
        <LocaleContainer>
          <Nav>{locales}</Nav>
        </LocaleContainer>
      </LoginContainer>
      <LoginImage />
    </Container>
  )
}

export default Login
